import useSWR, { mutate } from 'swr';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Lock, UserCheck, AlertTriangle } from 'lucide-react';
import Header from '../../../pages/Dashboard/Header';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ClipLoader } from 'react-spinners';
import SidebarApp from '../Roles/Modals/SidebarApp';
import AuthPercentageChart from '../Statistiques/succesConnecstat';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import FailedLoginsChart from '../Statistiques/echecConnectstat';
import LockedUsersChart from '../Statistiques/locked';
import InactiveUsersChart from '../Statistiques/Inactive User.';
import { useTranslation} from '../../../../TranslationContext';
import Navbar0 from '../../../../vitrine/Components/Navbar';

// Style CSS pour le carrousel
const carouselStyles = `
  .carousel .slide {
    background: transparent;
    padding: 10px;
  }
  .carousel .control-dots {
    bottom: -20px;
  }
  .carousel .control-dots .dot {
    box-shadow: none;
    background: #879bae;
    opacity: 0.4;
  }
  .carousel .control-dots .dot.selected {
    opacity: 1;
  }
`;

// Fetcher pour SWR
const axiosFetcher = async (url: string) => {
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

// Fonction pour calculer les statistiques
const calculateStats = (users: any[]) => {
  const activeUsers = users.filter(user => user.status === 'Active').length;
  const lockedAccounts = users.filter(user => user.status === 'Locked').length;
  const newUsers = users.filter(user => user.status === 'New').length;
  const failedLogins = users.filter(user => user.failureResponse !== '1960-06-01T00:00:00').length;

  return {
    activeUsers,
    lockedAccounts,
    newUsers,
    failedLogins,
  };
};

const COLORS = {
  primary: '#879bae',
  secondary: '#c06a70',
  text: '#3d3d3d',
  background: '#ffffff',
  success: '#4caf50',
  warning: '#ff9800',
  danger: '#f44336',
  info: '#2196f3',
  Additional: '#879bae'
};

// Composant de compteur animé
const AnimatedCounter = ({ value, duration = 1000 }: { value: number, duration?: number }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(count);
  const previousValue = useRef(0);
  
  useEffect(() => {
    if (value < previousValue.current) {
      setCount(0);
    }
    
    previousValue.current = value;
    countRef.current = count;
    
    if (value === 0) {
      setCount(0);
      return;
    }
    
    const startTime = Date.now();
    const endValue = value;
    const startValue = countRef.current;
    
    const counter = () => {
      const elapsedTime = Date.now() - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const easeOutQuart = (x: number): number => 1 - Math.pow(1 - x, 4);
      const easedProgress = easeOutQuart(progress);
      
      const currentValue = Math.floor(startValue + (endValue - startValue) * easedProgress);
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(counter);
      } else {
        setCount(endValue);
      }
    };
    
    requestAnimationFrame(counter);
  }, [value, duration]);
  
  return <>{count}</>;
};

export default function DashboardApp() {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const companyId = localStorage.getItem('company');
  const API_BASE_URL = import.meta.env.VITE_API;
  const [dataLoaded, setDataLoaded] = useState(false);
  const [sidebarCollapsed] = useState(false);
  // const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const { data: applicationData } = useSWR(
    companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}` : null,
    axiosFetcher,
    { revalidateOnFocus: true, revalidateOnReconnect: true, dedupingInterval: 10000 }
  );

  const { data: UserData, error: usersError, isLoading: usersLoading } = useSWR(
    companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}/users` : null,
    axiosFetcher
  );

  const stats = UserData ? calculateStats(UserData.data) : {
    activeUsers: 0,
    lockedAccounts: 0,
    newUsers: 0,
    failedLogins: 0,
  };

  useEffect(() => {
    if (UserData && !dataLoaded) {
      const timer = setTimeout(() => {
        setDataLoaded(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [UserData, dataLoaded]);

  useEffect(() => {
    if (UserData) {
      mutate(`${API_BASE_URL}/Company/${companyId}/applications/${appId}/stats`, stats);
    }
  }, [UserData, companyId, appId, stats]);

  if (!companyId) {
    return <div className="flex justify-center items-center h-full">{t('error_company_id_not_found')}</div>;
  }

  if (usersLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <ClipLoader color={COLORS.secondary} loading={true} size={50} />
      </div>
    );
  }

  if (usersError) {
    return <div className="flex justify-center items-center h-full">{t('error_loading_data')}</div>;
  }

  const statsDisplay = [
    { name: t('active_users'), value: stats.activeUsers, icon: UserCheck, color: 'bg-green-500', bgColor: 'bg-green-100' },
    { name: t('blocked_accounts'), value: stats.lockedAccounts, icon: Lock, color: 'bg-red-500', bgColor: 'bg-red-100' },
    { name: t('new_users'), value: stats.newUsers, icon: UserCheck, color: 'bg-yellow-500', bgColor: 'bg-yellow-100' },
    { name: t('connection_failures'), value: stats.failedLogins, icon: AlertTriangle, color: 'bg-blue-500', bgColor: 'bg-blue-100' },
  ];

  const chartData = [
    { name: t('active_users'), value: stats.activeUsers, color: COLORS.Additional },
    { name: t('connection_failures'), value: stats.failedLogins, color: COLORS.Additional },
    { name: t('new_users'), value: stats.newUsers, color: COLORS.Additional },
    { name: t('blocked_accounts'), value: stats.lockedAccounts, color: COLORS.Additional },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-md border border-gray-200">
          <p className="font-medium text-sm text-gray-700">{`${label}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-screen     bg-cover bg-fixed"
     style={{ backgroundImage: "url('/otentiCadena (2).jpg'" }}>
    <div className="absolute inset-0 bg-black opacity-10"></div>
     
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      <Navbar0/>
        <Header />
      </div>
      {/* <div className="flex flex-1 pt-16      relative h-screen bg-cover bg-fixed"
            style={{ backgroundImage: "url('/otentiCadena.Png')" }}/> */}
     <div className='relative z-10'>
        <div className={`fixed top-16 bottom-0 z-40 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
          <SidebarApp />
        </div>

        <div className={`flex-1 mt-27 overflow-y-auto transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
          <div className="p-6 space-y-6">
        
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-100">
              <h1 className="text-2xl font-medium text-gray-800 flex items-center">
                <span className="text-3xl font-bold text-gray-800 mr-2">
                  {applicationData?.data.displayName}
                </span>
                <span className="text-gray-500 text-xl">{t('dashboard')}</span>
              </h1>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {statsDisplay.map((stat) => (
                <div key={stat.name} className="bg-white overflow-hidden shadow-sm rounded-xl border border-gray-100 transition-all duration-300 hover:shadow-md">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 rounded-lg ${stat.bgColor} p-3`}>
                        <stat.icon className={`h-5 w-5 ${stat.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                          <dd className="text-2xl font-semibold text-gray-900">
                            {dataLoaded ? <AnimatedCounter value={stat.value} duration={1200} /> : 0}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
              <h2 className="text-xl font-medium text-gray-800 mb-6">{t('user_activity_overview')}</h2>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    barSize={60}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tick={{ fill: COLORS.text, fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis 
                      axisLine={false} 
                      tick={{ fill: COLORS.text, fontSize: 12 }}
                      tickLine={false}
                      width={50}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={1500}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-8">
                <style>{carouselStyles}</style>
                <h2 className="text-xl font-medium text-gray-800 mb-4">{t('additional_insights')}</h2>
                <Carousel
                  showArrows={true}
                  showStatus={false}
                  showThumbs={false}
                  infiniteLoop={true}
                  autoPlay={false}
                  interval={5000}
                  stopOnHover={true}
                  swipeable={true}
                  className="bg-white rounded-xl shadow-sm p-4 border border-gray-100"
                >
                  <div className="p-4"><AuthPercentageChart /></div>
                  <div className="p-4"><FailedLoginsChart /></div>
                  <div className="p-4"><LockedUsersChart /></div>
                  <div className="p-4"><InactiveUsersChart /></div>
                </Carousel>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    
  );
}


// import useSWR, { mutate } from 'swr';
// import axios from 'axios';
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
// import { Lock, UserCheck, AlertTriangle, Users } from 'lucide-react';
// import Header from '../../../pages/Dashboard/Header';
// import { useParams } from 'react-router-dom';
// import { useEffect, useState, useRef } from 'react';
// import { ClipLoader } from 'react-spinners';
// import SidebarApp from '../Roles/Modals/SidebarApp';
// import AuthPercentageChart from '../Statistiques/succesConnecstat';
// import { Carousel } from 'react-responsive-carousel';
// import 'react-responsive-carousel/lib/styles/carousel.min.css';
// import FailedLoginsChart from '../Statistiques/echecConnectstat';
// import LockedUsersChart from '../Statistiques/locked';
// import InactiveUsersChart from '../Statistiques/Inactive User.';

// // Style CSS pour le carrousel
// const carouselStyles = `
//   .carousel .slide {
//     background: transparent;
//     padding: 10px;
//   }
//   .carousel .control-dots {
//     bottom: -20px;
//   }
//   .carousel .control-dots .dot {
//     box-shadow: none;
//     background: #879bae;
//     opacity: 0.4;
//   }
//   .carousel .control-dots .dot.selected {
//     opacity: 1;
//   }
// `;

// // Fetcher pour SWR
// const axiosFetcher = async (url: string) => {
//   const response = await axios.get(url, {
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem('access_token')}`,
//     },
//   });
//   return response.data;
// };

// // Fonction pour calculer les statistiques
// const calculateStats = (users: any[]) => {
//   const activeUsers = users.filter(user => user.status === 'Active').length;
//   const lockedAccounts = users.filter(user => user.status === 'Locked').length;
//   const newUsers = users.filter(user => user.status === 'New').length;
//   const failedLogins = users.filter(user => user.failureResponse !== '1960-06-01T00:00:00').length;

//   return {
//     activeUsers,
//     lockedAccounts,
//     newUsers,
//     failedLogins,
//   };
// };

// // // Palette de couleurs personnalisée
// // const COLORS = {
// //   primary: '#879bae',
// //   secondary: '#c06a70',
// //   text: '#3d3d3d',
// //   background: '#ffffff',
// //   success: '#4caf50',
// //   warning: '#ff9800',
// //   danger: '#f44336',
// //   info: '#2196f3'
// // };
// const COLORS = {
//   primary: '#879bae',        // Gris-bleu pour les barres
//   secondary: '#c06a70',      // Rouge-rosé
//   text: '#3d3d3d',           // Gris foncé pour le texte
//   background: '#ffffff',     // Blanc pour l'arrière-plan
//   success: '#4caf50',        // Vert pour les utilisateurs actifs
//   warning: '#ff9800',        // Orange pour les nouveaux utilisateurs
//   danger: '#f44336',         // Rouge pour les comptes bloqués
//   info: '#2196f3' ,           // Bleu pour les échecs de connexion
//   Additional:'#879bae'
// };

// // Composant de compteur animé
// const AnimatedCounter = ({ value, duration = 1000 }: { value: number, duration?: number }) => {
//   const [count, setCount] = useState(0);
//   const countRef = useRef(count);
//   const previousValue = useRef(0);
  
//   useEffect(() => {
//     if (value < previousValue.current) {
//       setCount(0);
//     }
    
//     previousValue.current = value;
//     countRef.current = count;
    
//     if (value === 0) {
//       setCount(0);
//       return;
//     }
    
//     const startTime = Date.now();
//     const endValue = value;
//     const startValue = countRef.current;
    
//     const counter = () => {
//       const elapsedTime = Date.now() - startTime;
//       const progress = Math.min(elapsedTime / duration, 1);
      
//       const easeOutQuart = (x: number): number => 1 - Math.pow(1 - x, 4);
//       const easedProgress = easeOutQuart(progress);
      
//       const currentValue = Math.floor(startValue + (endValue - startValue) * easedProgress);
//       setCount(currentValue);
      
//       if (progress < 1) {
//         requestAnimationFrame(counter);
//       } else {
//         setCount(endValue);
//       }
//     };
    
//     requestAnimationFrame(counter);
//   }, [value, duration]);
  
//   return <>{count}</>;
// };

// export default function DashboardApp() {
//   const { appId } = useParams<{ appId: string }>();
//   const companyId = localStorage.getItem('company');
//   const API_BASE_URL = import.meta.env.VITE_API;
//   const [dataLoaded, setDataLoaded] = useState(false);
//   const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

//   // Utiliser SWR pour récupérer les données
//   const { data: applicationData } = useSWR(
//     companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}` : null,
//     axiosFetcher,
//     { revalidateOnFocus: true, revalidateOnReconnect: true, dedupingInterval: 10000 }
//   );

//   const { data: UserData, error: usersError, isLoading: usersLoading } = useSWR(
//     companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}/users` : null,
//     axiosFetcher
//   );

//   // Calculer les statistiques
//   const stats = UserData ? calculateStats(UserData.data) : {
//     activeUsers: 0,
//     lockedAccounts: 0,
//     newUsers: 0,
//     failedLogins: 0,
//   };

//   useEffect(() => {
//     if (UserData && !dataLoaded) {
//       const timer = setTimeout(() => {
//         setDataLoaded(true);
//       }, 300);
//       return () => clearTimeout(timer);
//     }
//   }, [UserData, dataLoaded]);

//   useEffect(() => {
//     if (UserData) {
//       mutate(`${API_BASE_URL}/Company/${companyId}/applications/${appId}/stats`, stats);
//     }
//   }, [UserData, companyId, appId, stats]);

//   if (!companyId) {
//     return <div className="flex justify-center items-center h-full">Error: Company ID not found.</div>;
//   }

//   if (usersLoading) {
//     return (
//       <div className="flex justify-center items-center h-full">
//         <ClipLoader color={COLORS.secondary} loading={true} size={50} />
//       </div>
//     );
//   }

//   if (usersError) {
//     return <div className="flex justify-center items-center h-full">Error loading data.</div>;
//   }

//   const statsDisplay = [
//     { name: 'Active users', value: stats.activeUsers, icon: UserCheck, color: 'bg-green-500', bgColor: 'bg-green-100' },
//     { name: 'Blocked accounts', value: stats.lockedAccounts, icon: Lock, color: 'bg-red-500', bgColor: 'bg-red-100' },
//     { name: 'New users', value: stats.newUsers, icon: UserCheck, color: 'bg-yellow-500', bgColor: 'bg-yellow-100' },
//     { name: 'Connection failures', value: stats.failedLogins, icon: AlertTriangle, color: 'bg-blue-500', bgColor: 'bg-blue-100' },
//   ];

//   const chartData = [
//     { name: 'Active users', value: stats.activeUsers, color: COLORS.Additional },
//     { name: 'Connection failures', value: stats.failedLogins, color: COLORS.Additional },
//     { name: 'New users', value: stats.newUsers, color: COLORS.Additional },
//     { name: 'Blocked accounts', value: stats.lockedAccounts, color: COLORS.Additional },
//   ];

//   const CustomTooltip = ({ active, payload, label }: any) => {
//     if (active && payload && payload.length) {
//       return (
//         <div className="bg-white p-4 shadow-lg rounded-md border border-gray-200">
//           <p className="font-medium text-sm text-gray-700">{`${label}: ${payload[0].value}`}</p>
//         </div>
//       );
//     }
//     return null;
//   };

//   return (
//     <div className="flex flex-col h-screen bg-gray-50">
//       {/* Header fixe */}
//       <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
//         <Header />
//       </div>

//       <div className="flex flex-1 pt-16">
//         {/* Sidebar fixe */}
//         <div className={`fixed top-16 bottom-0 z-40 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
//           <SidebarApp />
//         </div>

//         {/* Contenu principal scrollable */}
//         <div className={`flex-1 overflow-y-auto transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
//           <div className="p-6 space-y-6">
//             <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-100">
//               <h1 className="text-2xl font-medium text-gray-800 flex items-center">
//                 <span className="text-3xl font-bold text-gray-800 mr-2">
//                   {applicationData?.data.displayName}
//                 </span>
//                 <span className="text-gray-500 text-xl">Dashboard</span>
//               </h1>
//             </div>

//             <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
//               {statsDisplay.map((stat) => (
//                 <div key={stat.name} className="bg-white overflow-hidden shadow-sm rounded-xl border border-gray-100 transition-all duration-300 hover:shadow-md">
//                   <div className="p-5">
//                     <div className="flex items-center">
//                       <div className={`flex-shrink-0 rounded-lg ${stat.bgColor} p-3`}>
//                         <stat.icon className={`h-5 w-5 ${stat.color.replace('bg-', 'text-')}`} />
//                       </div>
//                       <div className="ml-5 w-0 flex-1">
//                         <dl>
//                           <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
//                           <dd className="text-2xl font-semibold text-gray-900">
//                             {dataLoaded ? <AnimatedCounter value={stat.value} duration={1200} /> : 0}
//                           </dd>
//                         </dl>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
//               <h2 className="text-xl font-medium text-gray-800 mb-6">User Activity Overview</h2>
//               <div className="h-96">
//                 <ResponsiveContainer width="100%" height="100%">
//                   <BarChart
//                     data={chartData}
//                     margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
//                     barSize={60}
//                   >
//                     <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
//                     <XAxis 
//                       dataKey="name" 
//                       axisLine={false} 
//                       tick={{ fill: COLORS.text, fontSize: 12 }}
//                       tickLine={false}
//                     />
//                     <YAxis 
//                       axisLine={false} 
//                       tick={{ fill: COLORS.text, fontSize: 12 }}
//                       tickLine={false}
//                       width={50}
//                     />
//                     <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
//                     <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={1500}>
//                       {chartData.map((entry, index) => (
//                         <Cell key={`cell-${index}`} fill={entry.color} />
//                       ))}
//                     </Bar>
//                   </BarChart>
//                 </ResponsiveContainer>
//               </div>

//               {/* Carrousel des graphiques supplémentaires */}
//               <div className="mt-8">
//                 <style>{carouselStyles}</style>
//                 <h2 className="text-xl font-medium text-gray-800 mb-4">Additional Insights</h2>
//                 <Carousel
//                   showArrows={true}
//                   showStatus={false}
//                   showThumbs={false}
//                   infiniteLoop={true}
//                   autoPlay={false}
//                   interval={5000}
//                   stopOnHover={true}
//                   swipeable={true}
//                   className="bg-white rounded-xl shadow-sm p-4 border border-gray-100"
//                 >
//                   <div className="p-4"><AuthPercentageChart /></div>
//                   <div className="p-4"><FailedLoginsChart /></div>
//                   <div className="p-4"><LockedUsersChart /></div>
//                   <div className="p-4"><InactiveUsersChart /></div>
//                 </Carousel>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }


